import { call, put, select } from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";

import {
  getNextEntityName,
  sanitizeV2RequestBody,
} from "@superblocksteam/shared";

import { getSagaErrorCode } from "core/utils/sagaError";
import { updatePartialApiInfo } from "legacy/actions/apiActions";
import { TriggerStepType } from "legacy/constants/ActionConstants";
import { ReduxActionTypes } from "legacy/constants/ReduxActionConstants";
import { getCurrentBranch } from "legacy/selectors/editorSelectors";
import { getAllEntityNames } from "legacy/selectors/sagaSelectors";
import { selectIsEditingWithAi } from "store/slices/ai/selectors";
import { getV2ApiId } from "store/slices/apisV2/utils/getApiIdAndName";
import { ROOT } from "store/utils/types";
import { signAndUpdateApi } from "utils/resource-signing";

import { API_STATUS_CODES } from "../../../../legacy/constants/ApiConstants";
import selectLastSuccessfulWrite from "../../../../legacy/selectors/successfulWriteSelector";
import logger from "../../../../utils/logger";
import {
  findAndMarshalProtoBlocks,
  findAndUnMarshalProtoBlocks,
} from "../../../../utils/marshalProto";
import { createSaga } from "../../../utils/saga";
import { lock } from "../../apisShared/sharedPersistApiLock";
import type * as BackendTypes from "../backend-types";
import { createV2Api } from "../client";
import slice, { type ApiDtoWithPb } from "../slice";
import { ApiTriggerType } from "../types";
import { getTriggerTypeFromApi, getUpdatedTime } from "../utils/api-utils";
/*
 *  Some API differences between v1 and v2
 *  - no separate creation endpoint for workflows and jobs in v2
 *
 */

export function* createV2ApiInternal({
  payload,
  pageId,
  prepopulatedName,
  replaceExistingUnsavedApiId,
  skipTriggers = false,
  skipRename = false,
}: {
  payload: BackendTypes.Api;
  pageId?: string;
  prepopulatedName?: string;
  replaceExistingUnsavedApiId?: string;
  skipTriggers?: boolean;
  skipRename?: boolean;
}): Generator<
  unknown,
  {
    result: ApiDtoWithPb;
    replaceExistingUnsavedApiId?: string;
  },
  any
> {
  let unlock = () => {};
  try {
    unlock = yield call(lock, payload.metadata.id);

    const isEditingWithAi: boolean = yield select(selectIsEditingWithAi);

    if (isEditingWithAi) {
      // We're editing with ai, so just return the api so we
      // store it in redux but don't save it to the server
      let name = payload.metadata.name;
      if (!name) {
        const allNames: string[] = yield select(getAllEntityNames);
        // this can happen if the user manually creates an api while there are ai edits
        name = getNextEntityName(prepopulatedName ?? "API", allNames);
      }
      payload.metadata.name = name;
      const tempApi = {
        id: payload.metadata.id,
        name,
        apiPb: payload,
        // add other props?
      } as ApiDtoWithPb;

      return {
        result: tempApi,
        replaceExistingUnsavedApiId: undefined,
      };
    }

    const triggerType = getTriggerTypeFromApi(payload);
    const lastSuccessfulWrite: Date = yield select(selectLastSuccessfulWrite);
    if (triggerType === ApiTriggerType.UI) {
      if (!pageId) throw new Error("PageId is required for UI trigger type");
      if (skipRename && prepopulatedName) {
        payload.metadata.name = prepopulatedName;
      } else {
        let name = payload.metadata.name;

        const allNames: string[] = yield select(getAllEntityNames);
        if (!name || name === "") {
          name = getNextEntityName(prepopulatedName ?? "API", allNames);
        } else if (name && allNames.includes(name)) {
          name = getNextEntityName(name, allNames);
        }

        payload.metadata.name = name;
      }
    }
    findAndMarshalProtoBlocks(payload);
    sanitizeV2RequestBody(payload);

    const branch: ReturnType<typeof getCurrentBranch> =
      yield select(getCurrentBranch);

    try {
      yield call(signAndUpdateApi, payload);
    } catch (e: any) {
      throw new Error("Failed to sign API");
    }

    const result: Awaited<ReturnType<typeof createV2Api>> = yield call(
      createV2Api,
      branch?.name,
      payload,
      pageId,
      lastSuccessfulWrite,
    );
    const { apiPb, updated } = result;
    if (apiPb == null) {
      throw new Error("apiPb was not found");
    }
    yield put({
      type: ReduxActionTypes.UPDATE_LAST_SUCCESSFUL_WRITE,
      payload: getUpdatedTime(updated),
    });

    const apiId = apiPb.metadata.id;

    if (!skipTriggers) {
      yield put(
        updatePartialApiInfo({
          id: apiId,
          data: {
            id: apiId,
            onError: [
              {
                id: uuidv4(),
                type: TriggerStepType.SHOW_ALERT,
                message: `{{${apiPb.metadata.name}.error}}`,
                style: "error",
              },
            ],
            dynamicTriggerPathList: [{ key: "onError" }],
          },
        }),
      );
    }

    return {
      result: result as ApiDtoWithPb,
      replaceExistingUnsavedApiId,
    };
  } catch (err) {
    logger.debug(`Errored when persisting the API: ${err}`);
    throw err;
  } finally {
    unlock();
  }
}

export const createV2ApiSaga = createSaga(
  createV2ApiInternal,
  "createV2ApiSaga",
  {
    sliceName: slice.name,
  },
);

slice.saga(createV2ApiSaga, {
  start(state, { payload }) {
    state.loading[ROOT] = true;
    delete state.errors[ROOT];
    // newly created APIs have no deps
  },
  success(state, { payload, meta }) {
    const result = payload.result;
    findAndUnMarshalProtoBlocks(result);

    state.entities[getV2ApiId(result)] = result;
    delete state.loading[ROOT];

    const v2ApiId = getV2ApiId(result);

    if (payload.replaceExistingUnsavedApiId) {
      state.meta[v2ApiId] = state.meta[payload.replaceExistingUnsavedApiId];
      state.meta[v2ApiId].dirty = false;
      state.meta[v2ApiId].saving = false;

      delete state.entities[payload.replaceExistingUnsavedApiId];
    } else {
      state.meta[v2ApiId] = {};
    }
  },
  error(state, { payload, meta }) {
    state.errors[ROOT] = { error: payload };
    delete state.loading[ROOT];

    if (getSagaErrorCode(payload) === API_STATUS_CODES.RESOURCE_CONFLICT) {
      state.errors.stale = { error: payload };
    }
  },
});
